/**
 * QuestAdmin.tsx - Admin > Integrations > Quest.
 *
 * Port of the legacy admin_quest_list.php (Quest Accounts),
 * admin_quest_provider_list.php (Quest Providers) and admin_quest_lab_list.php
 * (Quest Diagnostics: Lab Presets and Lab Codes, in QuestLabTabs) pages.
 * The first two manage the account-scoped Quest ordering config tables:
 *   - Accounts (aa_accounts_quest) - maps a practice facility to a Quest
 *     Sending Facility (8-digit) + Business Unit. SFTP/API credentials are
 *     hidden and inherited server-side. Sending Facility is immutable once
 *     saved (delete + re-create to change it).
 *   - Providers (aa_accounts_quest_providers) - links an internal user to a
 *     numeric Quest Provider ID (NPI).
 *
 * Modeled on VibrantAdmin: PageHeader + Tabs + DataTable + Modal + ConfirmDialog.
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): no Add button and no row actions
 * on any tab, and no empty-list hint inviting an add; nothing says why. An
 * account or provider row already shows every field its dialog holds, so
 * those rows open nothing. Presets and codes: see QuestLabTabs.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Building2, UserCog, ListChecks, Barcode } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Tabs } from '@/components/ui/Tabs';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn, DataTableAction } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import {
  listQuestAccountsAdmin,
  createQuestAccount,
  updateQuestAccount,
  deleteQuestAccount,
  listQuestProvidersAdmin,
  createQuestProvider,
  updateQuestProvider,
  deleteQuestProvider,
  type QuestAccountRow,
  type QuestProviderRow,
} from '@/services/labs.service';
import * as adminSvc from '@/services/admin.service';
import { buildUserChoices } from '@/lib/userChoices';
import { QuestCodesTab, QuestPresetsTab } from '@/components/admin/QuestLabTabs';

/** Business units offered by the legacy admin_quest_list.php <select>. */
const BUSINESS_UNITS = ['Default', 'WDL', 'PHP', 'NEL', 'DAL', 'DLO', 'STL', 'TMP', 'MET', 'PBL', 'SEA', 'SKB'] as const;
const BUSINESS_UNIT_OPTIONS = BUSINESS_UNITS.map((u) => ({ value: u, label: u }));

// ════════════════════════════════════════════════════════════════════════════
// Accounts tab
// ════════════════════════════════════════════════════════════════════════════

interface AccountForm {
  sendingFacility: string;
  businessUnit: string;
  facilityId: number | null;
}

function AccountModal({
  account,
  facilities,
  onClose,
  onSaved,
}: {
  account?: QuestAccountRow | null;
  facilities: adminSvc.Facility[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = account != null;
  const [form, setForm] = useState<AccountForm>(() =>
    isEdit
      ? { sendingFacility: account.sendingFacility, businessUnit: account.businessUnit || 'Default', facilityId: account.facilityId || null }
      : { sendingFacility: '', businessUnit: 'Default', facilityId: null },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const facilityOptions = facilities.filter((f) => f.active).map((f) => ({ value: f.facilityId, label: f.facilityName }));

  // Legacy validation: sending facility must be exactly 8 digits, business unit
  // + facility required. Sending facility is only validated/entered on create.
  const sendingFacilityValid = /^\d{8}$/.test(form.sendingFacility);
  const canSave = (isEdit || sendingFacilityValid) && !!form.businessUnit && !!form.facilityId;

  function set<K extends keyof AccountForm>(field: K, value: AccountForm[K]) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave || !form.facilityId) return;

    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updateQuestAccount(account.id, { businessUnit: form.businessUnit, facilityId: form.facilityId });
      } else {
        await createQuestAccount({
          sendingFacility: form.sendingFacility.trim(),
          businessUnit: form.businessUnit,
          facilityId: form.facilityId,
        });
      }
      onSaved();
      onClose();
    } catch {
      setError('Failed to save Quest account. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit Quest Account' : 'Add Quest Account'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !canSave} loading={saving}>
            {isEdit ? 'Save Changes' : 'Add Quest Account'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
        )}

        <FormField label="Facility" required>
          <Select<number>
            options={facilityOptions}
            value={form.facilityId}
            onChange={(v) => set('facilityId', v)}
            placeholder="Select a facility…"
            searchable
          />
        </FormField>

        <FormField label="Sending Facility" required>
          <Input
            value={form.sendingFacility}
            onChange={(e) => set('sendingFacility', e.target.value.replace(/\D/g, ''))}
            placeholder="8 digit facility code"
            maxLength={8}
            inputMode="numeric"
            disabled={isEdit}
            hint={isEdit ? 'Sending facility cannot be changed. Delete this account and create a new one to change it.' : undefined}
            required={!isEdit}
            autoFocus={!isEdit}
          />
        </FormField>

        <FormField label="Business Unit" required>
          <Select<string>
            options={BUSINESS_UNIT_OPTIONS}
            value={form.businessUnit}
            onChange={(v) => set('businessUnit', v ?? 'Default')}
            placeholder="Select a business unit…"
          />
        </FormField>
      </form>
    </Modal>
  );
}

function AccountsTab({ showCreate, onCreated }: { showCreate: boolean; onCreated: () => void }) {
  const locked = useAdminPageLevel() === 'locked';
  const [accounts, setAccounts] = useState<QuestAccountRow[]>([]);
  const [facilities, setFacilities] = useState<adminSvc.Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState<QuestAccountRow | null>(null);
  const [deleteItem, setDeleteItem] = useState<QuestAccountRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, facs] = await Promise.all([listQuestAccountsAdmin(), adminSvc.listFacilities()]);
      setAccounts(rows);
      setFacilities(facs);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDelete() {
    if (!deleteItem) return;
    await deleteQuestAccount(deleteItem.id);
    setDeleteItem(null);
    void load();
  }

  const columns: DataTableColumn<QuestAccountRow>[] = [
    {
      key: 'facilityName',
      header: 'Facility',
      sortable: true,
      render: (row) => <span className="font-medium text-slate-900">{row.facilityName || `Facility #${row.facilityId}`}</span>,
    },
    {
      key: 'sendingFacility',
      header: 'Sending Facility',
      sortable: true,
      render: (row) => <span className="font-mono text-sm text-slate-700">{row.sendingFacility}</span>,
    },
    {
      key: 'businessUnit',
      header: 'Business Unit',
      sortable: true,
      render: (row) => <span className="text-slate-800">{row.businessUnit}</span>,
    },
  ];

  // Locked: no row actions (the row already shows every field the dialog holds).
  const rowActions: DataTableAction<QuestAccountRow>[] = locked
    ? []
    : [
        { label: 'Edit', onClick: (row) => setEditItem(row) },
        { label: 'Delete', variant: 'danger', onClick: (row) => setDeleteItem(row) },
      ];

  return (
    <div className="p-6">
      <DataTable<QuestAccountRow>
        data={accounts}
        columns={columns}
        keyField="id"
        loading={loading}
        rowActions={rowActions}
        emptyTitle="No Quest accounts found"
        emptyDescription={locked ? '' : 'Add a Quest account to map a facility to its Quest sending facility and business unit.'}
      />

      {showCreate && !locked && <AccountModal facilities={facilities} onClose={onCreated} onSaved={load} />}
      {editItem && <AccountModal account={editItem} facilities={facilities} onClose={() => setEditItem(null)} onSaved={load} />}

      <ConfirmDialog
        isOpen={deleteItem !== null}
        title="Delete Quest Account"
        message={deleteItem ? `Are you sure you want to delete the Quest account for "${deleteItem.facilityName || deleteItem.sendingFacility}"?` : ''}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteItem(null)}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Providers tab
// ════════════════════════════════════════════════════════════════════════════

interface ProviderForm {
  providerId: string;
  userId: number | null;
}

function ProviderModal({
  provider,
  users,
  onClose,
  onSaved,
}: {
  provider?: QuestProviderRow | null;
  users: adminSvc.AdminUser[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = provider != null;
  const [form, setForm] = useState<ProviderForm>(() =>
    isEdit ? { providerId: provider.providerId, userId: provider.userId || null } : { providerId: '', userId: null },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // /admin/users includes deactivated staff; drop them, but keep whoever this
  // provider is already mapped to so an existing mapping stays visible.
  const userOptions = buildUserChoices(users, form.userId)
    .map((c) => ({ value: c.userId, label: c.label }));

  const providerIdValid = /^\d+$/.test(form.providerId.trim());
  const canSave = providerIdValid && !!form.userId;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave || !form.userId) return;

    const payload = { providerId: form.providerId.trim(), userId: form.userId };
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updateQuestProvider(provider.id, payload);
      } else {
        await createQuestProvider(payload);
      }
      onSaved();
      onClose();
    } catch {
      setError('Failed to save Quest provider. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit Quest Provider' : 'Add Quest Provider'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !canSave} loading={saving}>
            {isEdit ? 'Save Changes' : 'Add Quest Provider'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
        )}

        <FormField label="User" required>
          <Select<number>
            options={userOptions}
            value={form.userId}
            onChange={(v) => setForm((f) => ({ ...f, userId: v }))}
            placeholder="Select a user…"
            searchable
          />
        </FormField>

        <FormField label="Provider ID" required>
          <Input
            value={form.providerId}
            onChange={(e) => setForm((f) => ({ ...f, providerId: e.target.value.replace(/\D/g, '') }))}
            placeholder="Enter numeric provider ID (NPI)"
            inputMode="numeric"
            required
          />
        </FormField>
      </form>
    </Modal>
  );
}

function ProvidersTab({ showCreate, onCreated }: { showCreate: boolean; onCreated: () => void }) {
  const locked = useAdminPageLevel() === 'locked';
  const [providers, setProviders] = useState<QuestProviderRow[]>([]);
  const [users, setUsers] = useState<adminSvc.AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState<QuestProviderRow | null>(null);
  const [deleteItem, setDeleteItem] = useState<QuestProviderRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [provs, usrs] = await Promise.all([listQuestProvidersAdmin(), adminSvc.listUsers()]);
      setProviders(provs);
      setUsers(usrs);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDelete() {
    if (!deleteItem) return;
    await deleteQuestProvider(deleteItem.id);
    setDeleteItem(null);
    void load();
  }

  const columns: DataTableColumn<QuestProviderRow>[] = [
    {
      key: 'providerName',
      header: 'Provider',
      sortable: true,
      render: (row) => <span className="font-medium text-slate-900">{row.providerName || `User #${row.userId}`}</span>,
    },
    {
      key: 'providerId',
      header: 'Quest Provider ID',
      render: (row) => <span className="font-mono text-sm text-slate-700">{row.providerId}</span>,
    },
  ];

  // Locked: no row actions (the row already shows every field the dialog holds).
  const rowActions: DataTableAction<QuestProviderRow>[] = locked
    ? []
    : [
        { label: 'Edit', onClick: (row) => setEditItem(row) },
        { label: 'Delete', variant: 'danger', onClick: (row) => setDeleteItem(row) },
      ];

  return (
    <div className="p-6">
      <DataTable<QuestProviderRow>
        data={providers}
        columns={columns}
        keyField="id"
        loading={loading}
        rowActions={rowActions}
        emptyTitle="No Quest providers found"
        emptyDescription={locked ? '' : 'Add an ordering provider (Quest Provider ID linked to a user) to enable ordering.'}
      />

      {showCreate && !locked && <ProviderModal users={users} onClose={onCreated} onSaved={load} />}
      {editItem && <ProviderModal provider={editItem} users={users} onClose={() => setEditItem(null)} onSaved={load} />}

      <ConfirmDialog
        isOpen={deleteItem !== null}
        title="Delete Quest Provider"
        message={deleteItem ? `Are you sure you want to delete "${deleteItem.providerName || deleteItem.providerId}"?` : ''}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteItem(null)}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Page
// ════════════════════════════════════════════════════════════════════════════

export function QuestAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [activeTab, setActiveTab] = useState('accounts');
  const [showCreate, setShowCreate] = useState(false);

  const addLabels: Record<string, string> = {
    accounts: 'Add Quest Account',
    providers: 'Add Quest Provider',
    presets: 'Add Lab Preset',
    codes: 'Add Lab Code',
  };
  const addLabel = addLabels[activeTab] ?? 'Add Quest Account';

  return (
    <div>
      <div className="px-6 pt-6">
        <PageHeader
          title="Quest"
          actions={
            locked ? undefined : (
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4 mr-1" />
                {addLabel}
              </Button>
            )
          }
        />
      </div>

      <Tabs
        tabs={[
          { id: 'accounts', label: 'Accounts', icon: <Building2 className="h-4 w-4" />, content: <AccountsTab showCreate={showCreate && activeTab === 'accounts'} onCreated={() => setShowCreate(false)} /> },
          { id: 'providers', label: 'Providers', icon: <UserCog className="h-4 w-4" />, content: <ProvidersTab showCreate={showCreate && activeTab === 'providers'} onCreated={() => setShowCreate(false)} /> },
          { id: 'presets', label: 'Lab Presets', icon: <ListChecks className="h-4 w-4" />, content: <QuestPresetsTab showCreate={showCreate && activeTab === 'presets'} onCreated={() => setShowCreate(false)} /> },
          { id: 'codes', label: 'Lab Codes', icon: <Barcode className="h-4 w-4" />, content: <QuestCodesTab showCreate={showCreate && activeTab === 'codes'} onCreated={() => setShowCreate(false)} /> },
        ]}
        onChange={(id) => { setActiveTab(id); setShowCreate(false); }}
      />
    </div>
  );
}
